import { useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { TbArrowLeft, TbCheck, TbX } from "react-icons/tb";

const STEPS = [
  { id: "company", labelKey: "crossborder.kyc.stepCompany" },
  { id: "address", labelKey: "crossborder.kyc.stepAddress" },
  { id: "documents", labelKey: "crossborder.kyc.stepDocuments" },
  { id: "review", labelKey: "crossborder.kyc.stepReview" },
];

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] focus:ring-2 focus:ring-[#e7a73f]/20 dark:border-brand-dark-border dark:bg-brand-dark-elevated";

const Field = ({ label, value, onChange, type = "text" }) => (
  <div>
    <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</label>
    <input type={type} className={inputClass} value={value} onChange={(e) => onChange(e.target.value)} />
  </div>
);

export const CrossborderKycOnboardingModal = ({ open, onClose, customer, profile, onComplete }) => {
  const [stepIndex, setStepIndex] = useState(0);
  const [form, setForm] = useState({
    legalName: profile?.legalName ?? customer?.label ?? "",
    dba: profile?.dba ?? "",
    taxId: profile?.taxId ?? "",
    email: profile?.email ?? customer?.email ?? "",
    phone: profile?.phone ?? "",
    website: profile?.website ?? "",
    street: profile?.address?.street ?? "",
    city: profile?.address?.city ?? "",
    state: profile?.address?.state ?? "",
    zipCode: profile?.address?.zipCode ?? "",
  });
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    setStepIndex(0);
    setSubmitted(false);
    onClose();
  };

  const handleSubmit = () => {
    setSubmitted(true);
    onComplete?.(form);
  };

  const currentStep = STEPS[stepIndex];
  const isLastStep = stepIndex === STEPS.length - 1;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm" PaperProps={{ className: "!rounded-2xl app-card" }}>
      <div className="flex items-start justify-between border-b border-gray-100 px-5 py-4 dark:border-brand-dark-border">
        <div>
          <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
            {intl.get("crossborder.kyc.modalTitle")}
          </h2>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{customer?.label}</p>
        </div>
        <button type="button" onClick={handleClose} className="btn-outline-icon" aria-label={intl.get("common.close")}>
          <TbX />
        </button>
      </div>

      {submitted ? (
        <div className="px-5 py-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
            <TbCheck className="h-6 w-6" />
          </div>
          <h3 className="text-base font-bold text-brand-dark dark:text-gray-100">
            {intl.get("crossborder.kyc.submittedTitle")}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("crossborder.kyc.submittedDescription")}
          </p>
          <button type="button" className="btn-primary mt-5" onClick={handleClose}>
            {intl.get("common.close")}
          </button>
        </div>
      ) : (
        <>
          <div className="border-b border-gray-100 px-5 py-3 dark:border-brand-dark-border">
            <div className="flex gap-2">
              {STEPS.map((step, index) => (
                <div
                  key={step.id}
                  className={`h-1 flex-1 rounded-full ${
                    index <= stepIndex ? "bg-[#e7a73f]" : "bg-gray-200 dark:bg-brand-dark-border"
                  }`}
                />
              ))}
            </div>
            <p className="mt-2 text-xs font-semibold text-gray-500">
              {intl.get(currentStep.labelKey)} ({stepIndex + 1}/{STEPS.length})
            </p>
          </div>

          <div className="space-y-4 px-5 py-4">
            {currentStep.id === "company" && (
              <>
                <Field
                  label={intl.get("crossborder.kyc.legalName")}
                  value={form.legalName}
                  onChange={(v) => setForm((prev) => ({ ...prev, legalName: v }))}
                />
                <Field
                  label={intl.get("crossborder.kyc.dba")}
                  value={form.dba}
                  onChange={(v) => setForm((prev) => ({ ...prev, dba: v }))}
                />
                <Field
                  label={intl.get("crossborder.kyc.taxId")}
                  value={form.taxId}
                  onChange={(v) => setForm((prev) => ({ ...prev, taxId: v }))}
                />
                <Field
                  label={intl.get("crossborder.kyc.email")}
                  value={form.email}
                  onChange={(v) => setForm((prev) => ({ ...prev, email: v }))}
                  type="email"
                />
                <Field
                  label={intl.get("crossborder.kyc.phone")}
                  value={form.phone}
                  onChange={(v) => setForm((prev) => ({ ...prev, phone: v }))}
                />
              </>
            )}

            {currentStep.id === "address" && (
              <>
                <Field
                  label={intl.get("crossborder.kyc.street")}
                  value={form.street}
                  onChange={(v) => setForm((prev) => ({ ...prev, street: v }))}
                />
                <div className="grid grid-cols-2 gap-3">
                  <Field
                    label={intl.get("crossborder.kyc.city")}
                    value={form.city}
                    onChange={(v) => setForm((prev) => ({ ...prev, city: v }))}
                  />
                  <Field
                    label={intl.get("crossborder.kyc.state")}
                    value={form.state}
                    onChange={(v) => setForm((prev) => ({ ...prev, state: v }))}
                  />
                </div>
                <Field
                  label={intl.get("crossborder.kyc.zipCode")}
                  value={form.zipCode}
                  onChange={(v) => setForm((prev) => ({ ...prev, zipCode: v }))}
                />
              </>
            )}

            {currentStep.id === "documents" && (
              <div className="rounded-xl border border-dashed border-gray-200 p-6 text-center dark:border-brand-dark-border">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {intl.get("crossborder.kyc.documentsPlaceholder")}
                </p>
              </div>
            )}

            {currentStep.id === "review" && (
              <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
                <p>
                  <span className="font-semibold">{intl.get("crossborder.kyc.legalName")}:</span> {form.legalName}
                </p>
                <p>
                  <span className="font-semibold">{intl.get("crossborder.kyc.taxId")}:</span> {form.taxId}
                </p>
                <p>
                  <span className="font-semibold">{intl.get("crossborder.kyc.email")}:</span> {form.email}
                </p>
                <p>
                  <span className="font-semibold">{intl.get("crossborder.kyc.street")}:</span> {form.street}
                </p>
              </div>
            )}
          </div>

          <div className="flex justify-between border-t border-gray-100 px-5 py-4 dark:border-brand-dark-border">
            {stepIndex > 0 ? (
              <button
                type="button"
                className="btn-back"
                onClick={() => setStepIndex((i) => i - 1)}
              >
                <TbArrowLeft className="h-4 w-4" />
                {intl.get("nav.back")}
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              className="btn-primary"
              onClick={() => (isLastStep ? handleSubmit() : setStepIndex((i) => i + 1))}
            >
              {isLastStep ? intl.get("crossborder.kyc.submit") : intl.get("firstAccess.continue")}
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
};
